import {
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    MenuRounded as MenuRoundedIcon,
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import MuiDrawer from '@mui/material/Drawer';
import Grid from '@mui/material/GridLegacy';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import is from '@sindresorhus/is';
import { Fragment, useContext, useEffect, useState } from 'react';

import {
    createAlignmentRecordItem,
    createAnalyticsDashboardListItem,
    createControlRoomListItem,
    createJobArchitectureListItem,
    createJobProfileManagementListItem,
    createLearningManagementListItem,
    createPositionManagementItem,
    createTalentDirectoryListItem,
} from './navigation-menu-list-item';
import { UserMenu } from './UserMenu';
import { VisibilityIndicator } from './VisibilityIndicator';
import { ApplicationContext } from '../../../../shared/source/context/context';
import { useLearningOpportunitiesPermissions } from '../../../../shared/source/query-hooks/learning-opportunities';
import { CompanyLogo } from '../../design/components/CompanyLogo';
import { Navigation } from '../../design/components/Navigation';
import { GlobalSearchBar } from '../global-search/GlobalSearch';
import { route as dashboardPageRoute } from '../pages/dashboard/constants';
import { route as myGrowthPageRoute } from '../pages/my-growth/constants';
import { route as profilePageRoute } from '../pages/profile/constants';
import { useFeaturedLearningOpportunityOptions } from '../query-hooks/featured-learning-opportunities';
import { useGlobalPermissions } from '../query-hooks/global-permissions';
import { useJobArchitectureOptionsQuery } from '../query-hooks/job-architecture-options';
import { useJobProfilesPermissions } from '../query-hooks/job-profiles/job-profiles';
import { useJobsPermissions } from '../query-hooks/jobs';
import { useMyProfilePermissionsQuery } from '../query-hooks/profile';
import { useTalentDirectoryPermissions } from '../query-hooks/talent-directory/talent-directory';
import { useBusinessIntelligencePermissions } from '../query-hooks/use-business-intelligence-options';
import { useUserBookmarksPermission } from '../query-hooks/users/bookmark';

import type {
    SideDrawerLinkProperties,
    SideDrawerListItem,
    SideDrawerParentLinkProperties,
} from './navigation-menu-list-item';
import type { RouteConfig } from '../../design/components/Navigation';
import type { FunctionComponent, MouseEventHandler, SyntheticEvent } from 'react';
import type { Just } from 'true-myth/maybe';

const isSideDrawerParentLink = (link: SideDrawerListItem): link is SideDrawerParentLinkProperties => {
    return 'children' in link && is.array(link.children);
};

export const SideDrawerLink: FunctionComponent<SideDrawerLinkProperties> = (props) => {
    const {
        actions: { navigateTo },
    } = useContext(ApplicationContext);
    const { externalUrl, Icon, label, onRouteChange, path } = props;

    return (
        <ListItemButton
            sx={{
                paddingX: 1,
            }}
            onClick={() => {
                if (externalUrl) {
                    window.open(path, '_blank');
                } else {
                    navigateTo(path);
                }
                onRouteChange?.();
            }}
        >
            <ListItemIcon>
                <Icon color="action" />
            </ListItemIcon>
            <ListItemText>{label}</ListItemText>
        </ListItemButton>
    );
};

type SideDrawerLinkListProps = {
    readonly links: readonly SideDrawerListItem[];
};

export const SideDrawerLinkList: FunctionComponent<SideDrawerLinkListProps> = ({ links }) => {
    const [isListItemsOpen, setIsListItemsOpen] = useState<Record<string, boolean>>(
        links
            .filter(isSideDrawerParentLink)
            .map((link) => link.label)
            .reduce((acc, label) => ({ ...acc, [label]: false }), {}),
    );

    const setIsListItemOpen = (label: string, isOpen: boolean): void => {
        setIsListItemsOpen((prev) => ({ ...prev, [label]: isOpen }));
    };

    return (
        <Fragment>
            {links.map((link) => {
                if (isSideDrawerParentLink(link)) {
                    return (
                        <Fragment key={link.label}>
                            <ListItemButton
                                aria-label={link.label}
                                sx={{
                                    paddingX: 1,
                                }}
                                onClick={() => {
                                    setIsListItemOpen(link.label, isListItemsOpen[link.label] === false);
                                }}
                            >
                                <ListItemIcon>
                                    <link.Icon color="action" />
                                </ListItemIcon>
                                <ListItemText>{link.label}</ListItemText>
                                {isListItemsOpen[link.label] === true ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </ListItemButton>
                            <Collapse in={isListItemsOpen[link.label]} timeout="auto" unmountOnExit={true}>
                                <Box ml={2}>
                                    <SideDrawerLinkList links={link.children} />
                                </Box>
                            </Collapse>
                        </Fragment>
                    );
                }

                return (
                    <SideDrawerLink
                        key={link.label}
                        externalUrl={link.externalUrl}
                        Icon={link.Icon}
                        label={link.label}
                        NewIcon={link.NewIcon}
                        path={link.path}
                        onRouteChange={link.onRouteChange}
                    />
                );
            })}
        </Fragment>
    );
};
export const SideDrawerNavigation: FunctionComponent = () => {
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const {
        applicationState: { customization, featureFlagService, isAssumedTenant },
        translate,
    } = useContext(ApplicationContext);
    const { canUserManageJobs } = useJobsPermissions();
    const { canAccessTalentDirectory } = useTalentDirectoryPermissions();
    const jobArchitectureOptions = useJobArchitectureOptionsQuery();
    const { canUserFilterLearningOpportunities } = useLearningOpportunitiesPermissions();
    const { canUserGetJobProfiles } = useJobProfilesPermissions();
    const { canAccessBusinessIntelligence } = useBusinessIntelligencePermissions();
    const { data: myProfilePermissions } = useMyProfilePermissionsQuery();
    const canUseControlRoom = myProfilePermissions?.canUseControlRoom ?? false;
    const tenantId = customization.get('tenantId');

    const canUserGetJobArchitecture =
        jobArchitectureOptions.isSuccess && jobArchitectureOptions.data.isOk
            ? jobArchitectureOptions.data.value.canUserGetJobArchitecture
            : false;

    const closeMenu = (): void => {
        setIsDrawerOpen(false);
    };

    const openMenu: MouseEventHandler<HTMLElement> = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDrawerOpen(true);
    };

    const sideDrawerLinksConfig: readonly SideDrawerListItem[] = [
        createAnalyticsDashboardListItem({ canAccessBusinessIntelligence, onRouteChange: closeMenu, translate }),
        createJobProfileManagementListItem({
            canUserGetJobProfiles,
            canUserManageJobs,
            onRouteChange: closeMenu,
            translate,
        }),
        createLearningManagementListItem({ canUserFilterLearningOpportunities, onRouteChange: closeMenu, translate }),
        createTalentDirectoryListItem({ canAccessTalentDirectory, onRouteChange: closeMenu, translate }),
        createJobArchitectureListItem({
            canUserGetJobArchitecture,
            featureFlagService,
            onRouteChange: closeMenu,
            translate,
        }),
        createPositionManagementItem({ featureFlagService, onRouteChange: closeMenu, translate }),
        createAlignmentRecordItem({ featureFlagService, onRouteChange: closeMenu, translate }),
        createControlRoomListItem({
            canUseControlRoom,
            isAssumedTenant,
            onRouteChange: closeMenu,
            tenantId,
            translate,
        }),
    ]
        .filter((sideDrawerListItem): sideDrawerListItem is Just<SideDrawerListItem> => {
            return sideDrawerListItem.isJust;
        })
        .map((sideDrawerListItem) => {
            return sideDrawerListItem.value;
        });

    return (
        <Fragment>
            {sideDrawerLinksConfig.length > 0 && (
                <IconButton aria-label={translate('side-navigation.opener-alt-text')} onClick={openMenu}>
                    <MenuRoundedIcon />
                </IconButton>
            )}
            <MuiDrawer anchor="left" hideBackdrop={true} open={isDrawerOpen}>
                <ClickAwayListener onClickAway={closeMenu}>
                    <Box aria-label={translate('side-navigation.aria-label')} height="100%" p={3} pt={1} width="320px">
                        <Grid alignItems="center" container={true} gap={2} item={true} mb={3}>
                            <IconButton onClick={closeMenu}>
                                <MenuRoundedIcon />
                            </IconButton>
                            <CompanyLogo />
                        </Grid>
                        <Grid container={true} flexDirection="column">
                            <SideDrawerLinkList links={sideDrawerLinksConfig} />
                        </Grid>
                    </Box>
                </ClickAwayListener>
            </MuiDrawer>
        </Fragment>
    );
};

const buildRouterConfigForNavigationPages = (
    showGrowthPage: boolean,
    translate: (key: string) => string,
): readonly RouteConfig[] => {
    const baseConfig: readonly RouteConfig[] = [
        {
            href: dashboardPageRoute.patterns.dashboard,
            label: translate('navigation.dashboard'),
        },
        {
            href: profilePageRoute.patterns.users,
            label: translate('navigation.profile'),
        },
    ];

    if (!showGrowthPage) {
        return baseConfig;
    }

    return [
        ...baseConfig,
        {
            href: myGrowthPageRoute.patterns.myGrowth,
            label: translate('navigation.my-growth'),
        },
    ];
};

const calculateActiveIndexForNavigationPages = (
    pathname: string,
    routerConfig: readonly RouteConfig[],
): number | null => {
    const routeIndex = routerConfig.findIndex(({ href }) => pathname.startsWith(href));

    return routeIndex > -1 ? routeIndex : null;
};

export const NavigationPages: FunctionComponent = () => {
    const { applicationState, translate } = useContext(ApplicationContext);
    const {
        location: { pathname },
        myUserProfile,
    } = applicationState;
    const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
    const myUserProfileId = myUserProfile.get('id').unwrapOr('');
    const isMe = is.nonEmptyString(myUserProfileId) ? pathname.includes(myUserProfileId) : false;
    const { data: featuredOptions } = useFeaturedLearningOpportunityOptions(myUserProfileId);
    const { data: permissions } = useUserBookmarksPermission(myUserProfileId);
    const showFeaturedLearningOpportunities = featuredOptions?.canReadUserFeaturedOpportunities ?? false;
    const showBookmarks = permissions?.canReadBookmarks === true;
    const showGrowthPage = showFeaturedLearningOpportunities && showBookmarks;

    const routerConfig = buildRouterConfigForNavigationPages(showGrowthPage, translate);
    const index = calculateActiveIndexForNavigationPages(pathname, routerConfig);

    useEffect(() => {
        if (!isMe && pathname.startsWith(profilePageRoute.patterns.users)) {
            setActiveTabIndex(null);

            return;
        }

        setActiveTabIndex(index);
    }, [index, isMe, pathname]);

    const myUserProfileExists = myUserProfile.get('exists').unwrapOr(undefined);
    if (myUserProfileExists === false) {
        return null;
    }

    const handleChange = (_event: SyntheticEvent, newValue: number): void => {
        setActiveTabIndex(newValue);
    };

    return (
        <Box alignSelf="stretch" display="flex" gap={1} sx={{ minWidth: 'max-content' }}>
            <Navigation activeIndex={activeTabIndex} routerConfig={routerConfig} onChange={handleChange} />
        </Box>
    );
};

export const NavigationMenu: FunctionComponent = () => {
    const { data: globalPermissions, isSuccess } = useGlobalPermissions();

    return (
        <Grid alignItems="center" container={true} direction="row" flexWrap="nowrap" justifyContent="space-between">
            <NavigationPages />
            {isSuccess && globalPermissions.canUserSearch && <GlobalSearchBar />}
            <Box display="flex" gap={1}>
                <VisibilityIndicator />
                <UserMenu />
            </Box>
        </Grid>
    );
};
